/**
 * Hook for finding discovered child WebApplications for an asset.
 *
 * Reuses the shared child-assets relationship query and filters results
 * down to WebApplication nodes.
 */

import { isWebApplication } from '@/hooks/useAssets';
import { Asset, TreeNode } from '@/types';

import { useChildAssetsQuery } from './useChildAssetsQuery';

const isWebApplicationAsset = (item: unknown): item is Asset => {
  if (!item || typeof item !== 'object') return false;

  const candidate = item as Record<string, unknown>;
  const labels = Array.isArray(candidate.labels)
    ? (candidate.labels as string[])
    : [];

  return (
    typeof candidate.key === 'string' &&
    (candidate.key.startsWith('#webapplication#') ||
      labels.includes('WebApplication') ||
      candidate.label === 'WebApplication')
  );
};

const extractWebApplicationsFromTreeNode = (treeNode: TreeNode): Asset[] => {
  const result = treeNode.node?.result as Record<string, unknown> | undefined;
  if (!result) return [];

  const webApplications: Asset[] = [];

  for (const value of Object.values(result)) {
    if (!Array.isArray(value)) continue;
    webApplications.push(...value.filter(isWebApplicationAsset));
  }

  return webApplications;
};

export function useRelatedWebApplication(asset: Asset) {
  const { data, isLoading, status } = useChildAssetsQuery(asset.key);

  const relatedWebApplications =
    !isWebApplication(asset) && data
      ? Array.from(
          new Map(
            data
              .flatMap(extractWebApplicationsFromTreeNode)
              .filter(webApplication => webApplication.key !== asset.key)
              .map(webApplication => [webApplication.key, webApplication])
          ).values()
        )
      : [];

  return {
    relatedWebApplications,
    isLoading,
    status,
  };
}
